import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import * as mediasoupClient from 'mediasoup-client';
import { useSocket } from './SocketContext';

const WebRTCContext = createContext();

export const WebRTCProvider = ({ children, meetMode=false }) => {
  const { socket } = useSocket();

  const [isConnecting, setIsConnecting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isReconnectingStream, setIsReconnectingStream] = useState(false);

  const [cams, setCams] = useState([]);
  const [autoConnect, setAutoConnect] = useState(false);
  const [layoutPreset, setLayoutPreset] = useState(null);

  const [scheduledJob, setScheduledJob] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);

  const sendTransportRef = useRef(null);
  const localProducersRef = useRef({});
  const localStreamRef = useRef(null);
  const localCamIdRef = useRef(null);
  const localPublisherIdRef = useRef(null);
  const localVideoDeviceIdRef = useRef(null);
  const localVideoEnabledRef = useRef(false);
  const localAudioEnabledRef = useRef(false);
  const isParticipantRef = useRef(false);

  const [isParticipant, setIsParticipant] = useState(false);
  const [isJoiningParticipant, setIsJoiningParticipant] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedCamIds, setRecordedCamIds] = useState([]);
  const [recordingStartedAt, setRecordingStartedAt] = useState(null);

  const clearScheduledJobLocal = () => {
    setScheduledJob(null);
    setCurrentJob(null);
  };

  useEffect(() => {
    (async () => {
      await fetch(`${process.env.REACT_APP_ADDRESS}streaming/get_scheduled_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
        .then((response) => response.json())
        .then((response) => setScheduledJob(response.job ?? null));
    })();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);

      // Clear scheduled job once it's finished to avoid stale UI display
      if (scheduledJob && scheduledJob.end !== null && now > scheduledJob.end) {
        setScheduledJob(null);
        setCurrentJob(null);
        return;
      }

      setCurrentJob((prev) => {
        const activeJob = scheduledJob && now >= scheduledJob.start && (now <= scheduledJob.end || scheduledJob.end === null) ? scheduledJob : null;

        if (prev?.start === activeJob?.start && prev?.end === activeJob?.end)
          return prev; // Prevent from re-render if no change

        return activeJob;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [scheduledJob, setScheduledJob]);

  const [steps, setSteps] = useState([
    { name: 'Connexion au système', href: '#', status: 'upcoming' },
    { name: 'Démarrage des caméras', href: '#', status: 'upcoming' },
    { name: 'Réception des flux', href: '#', status: 'upcoming' },
  ]);

  const updateStepsStatus = useCallback((status1=null, status2=null, status3=null) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) => {
        if (i === 0 && status1 !== null) return { ...step, status: status1 }
        if (i === 1 && status2 !== null) return { ...step, status: status2 }
        if (i === 2 && status3 !== null) return { ...step, status: status3 }
        return step;
      })
    )
  }, []);

  const videoElsRef = useRef({});
  const audioElsRef = useRef({});
  const camAlertedRef = useRef({});
  const mediasoupDeviceRef = useRef(null);
  const mediasoupDevicePromiseRef = useRef(null);
  const recvTransportsRef = useRef({});
  const consumersRef = useRef({});
  const streamReleasedRef = useRef({});
  const localCaptureClaimedRef = useRef(false);
  const connectingCamsRef = useRef({});

  const raiseCamError = (camId, reason) => {
    if (!camId) return;
    setIsError(true);

    if (camAlertedRef.current[camId]) return;
    camAlertedRef.current[camId] = true;

    cleanupCam(camId);
  };

  const emitMedia = useCallback((event, data = {}) => {
    return new Promise((resolve, reject) => {
      if (!socket?.connected) {
        reject(new Error('Socket is not connected'));
        return;
      }

      socket.emit(event, data, (response) => {
        if (!response?.success) {
          const error = new Error(response?.error || `Media signaling error: ${event}`);
          error.code = response?.code;
          reject(error);
          return;
        }

        resolve(response);
      });
    });
  }, [socket]);

  const getMediasoupDevice = async () => {
    if (mediasoupDeviceRef.current) return mediasoupDeviceRef.current;

    if (!mediasoupDevicePromiseRef.current) {
      mediasoupDevicePromiseRef.current = (async () => {
        const response = await emitMedia('media_get_router_rtp_capabilities');
        const device = await mediasoupClient.Device.factory();

        await device.load({ routerRtpCapabilities: response.rtpCapabilities });

        mediasoupDeviceRef.current = device;

        return device;
      })();
    }

    try {
      return await mediasoupDevicePromiseRef.current;
    } catch (error) {
      mediasoupDevicePromiseRef.current = null;
      throw error;
    }
  };

  const detectCameras = useCallback(async (runScan=true) => {
    try {
      let streams = [];

      if (runScan) {
        setIsConnecting(true);
        updateStepsStatus('current', 'upcoming', 'upcoming');

        const claimResponse = await emitMedia('media_claim_local_capture');
        localCaptureClaimedRef.current = true;

        if (claimResponse.alreadyActive) updateStepsStatus('complete', 'complete', 'current');
        else updateStepsStatus('complete', 'current', 'upcoming');

        if (!claimResponse.alreadyActive) {
          let resScan;

          try {
            resScan = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/scan`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
            });

            if (!resScan.ok) {
              const txt = await resScan.text();
              throw new Error(`scan: HTTP ${resScan.status} - ${txt}`);
            }
          } catch (err) {
            updateStepsStatus('error', null, null);
            throw new Error("scan error");
          }
        }

        const deadline = Date.now() + 5000;

        while (Date.now() < deadline) {
          const response = await emitMedia('media_get_streams');

          streams = (response.streams || []).filter((stream) => stream.source === 'mediasoup-local');

          if (streams.length > 0) break;

          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      } else {
        const response = await emitMedia('media_get_streams');
        streams = response.streams || [];

        if (streams.length > 0) {
          setIsConnecting(true);
          updateStepsStatus('complete', 'complete', 'current');
        }
      }

      if (streams.length > 0) {
        updateStepsStatus(null, 'complete', 'current');

        const nextCams = streams.map((s, idx) => ({
          id: String(s.cam_id),
          label: `Cam ${idx}`,
          source: s.source,
          opened: Boolean(s.opened),
          kind: s.kind,
        }));

        setCams(nextCams);
        setAutoConnect(true);

        return nextCams;
      }

      if (!runScan) {
        setCams([]);
        setIsConnecting(false);
        updateStepsStatus('upcoming', 'upcoming', 'upcoming');
        return [];
      }

      updateStepsStatus(null, 'error', null);
      throw new Error("streams: no stream");
    } catch (e) {
      if (runScan && localCaptureClaimedRef.current) {
        if (socket?.connected) socket.emit('media_release_local_capture', {});
        localCaptureClaimedRef.current = false;
      }

      setIsConnecting(false);
      throw e;
    }
  }, [emitMedia, updateStepsStatus]);

  const getPublisherId = () => {
    const key = 'lineware_publisher_id';
    let publisherId = localStorage.getItem(key);

    if (!publisherId) {
      publisherId = crypto.randomUUID();
      localStorage.setItem(key, publisherId);
    }

    return publisherId;
  };

  const getDisplayName = () => {
    const token = localStorage.getItem('token');
    if (!token) return '';

    try {
      const value = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = value.padEnd(value.length + (4 - value.length % 4) % 4, '=');
      const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
      const payload = JSON.parse(new TextDecoder().decode(bytes));

      return [payload.firstname, payload.lastname].filter(Boolean).join(' ').trim() || payload.username || '';
    } catch {
      return '';
    }
  };

  const closeLocalParticipantTransport = () => {
    Object.values(localProducersRef.current).forEach((producer) => {
      if (producer && !producer.closed) producer.close();
    });

    localProducersRef.current = {};

    if (sendTransportRef.current) {
      sendTransportRef.current.close();
      sendTransportRef.current = null;
    }
  };

  const closeLocalParticipantMedia = () => {
    closeLocalParticipantTransport();

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    localVideoEnabledRef.current = false;
    localAudioEnabledRef.current = false;

    const camId = localCamIdRef.current;
    const videoEl = camId ? videoElsRef.current[camId] : null;
    if (videoEl) videoEl.srcObject = null;
  };

  const refreshLocalVideo = () => {
    const camId = localCamIdRef.current;
    const videoEl = camId ? videoElsRef.current[camId] : null;
    if (!videoEl) return;

    const videoTracks = localStreamRef.current?.getVideoTracks() || [];
    videoEl.srcObject = new MediaStream(videoTracks);

    const p = videoEl.play?.();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  const registerVideoEl = (camId, el) => {
    if (!camId || !el) return;

    videoElsRef.current[camId] = el;

    const track = localCamIdRef.current === camId
      ? localStreamRef.current?.getVideoTracks()[0]
      : consumersRef.current[camId]?.video?.track;

    const currentTrack = el.srcObject?.getVideoTracks?.()[0];

    if (!track || currentTrack?.id === track.id) return;

    el.srcObject = new MediaStream([track]);

    const p = el.play?.();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  const registerAudioEl = (camId, el) => {
    if (!camId || !el) return;

    audioElsRef.current[camId] = el;

    const consumer = consumersRef.current[camId]?.audio;
    const currentTrack = el.srcObject?.getAudioTracks?.()[0];

    if (consumer?.track && currentTrack?.id !== consumer.track.id) {
      el.srcObject = new MediaStream([consumer.track]);

      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  };

  const createParticipantSendTransport = async (camId, publisherId) => {
    const device = await getMediasoupDevice();
    const transportResponse = await emitMedia('media_create_send_transport', { camId, publisherId });
    const sendTransport = device.createSendTransport(transportResponse.transportOptions);

    sendTransportRef.current = sendTransport;

    sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await emitMedia('media_connect_send_transport', {
          camId,
          publisherId,
          transportId: sendTransport.id,
          dtlsParameters
        });

        callback();
      } catch (error) {
        errback(error);
      }
    });

    sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
      try {
        const response = await emitMedia('media_produce', {
          camId,
          publisherId,
          transportId: sendTransport.id,
          kind,
          rtpParameters,
          appData
        });

        callback({ id: response.producerId });
      } catch (error) {
        errback(error);
      }
    });

    return sendTransport;
  };

  const publishLocalParticipantTracks = async (sendTransport, camId, publisherId) => {
    const videoTrack = localStreamRef.current?.getVideoTracks().find((track) => track.readyState === 'live');
    const audioTrack = localStreamRef.current?.getAudioTracks().find((track) => track.readyState === 'live');

    if (videoTrack) {
      localProducersRef.current.video = await sendTransport.produce({
        track: videoTrack,
        stopTracks: false,
        appData: { camId, publisherId, enabled: localVideoEnabledRef.current }
      });
    }

    if (audioTrack) {
      localProducersRef.current.audio = await sendTransport.produce({
        track: audioTrack,
        stopTracks: false,
        codecOptions: { opusDtx: false },
        appData: { camId, publisherId, enabled: localAudioEnabledRef.current }
      });
    }
  };

  const joinParticipant = async () => {
    if (isParticipant || isJoiningParticipant) return;

    setIsJoiningParticipant(true);

    const publisherId = getPublisherId();
    const camId = `external-${publisherId}`;
    const displayName = getDisplayName();

    try {
      await emitMedia('media_claim_publisher', { camId, publisherId, displayName });

      localCamIdRef.current = camId;
      localPublisherIdRef.current = publisherId;

      const [videoResult, audioResult] = await Promise.allSettled([
        navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        }),
        navigator.mediaDevices.getUserMedia({ video: false, audio: true })
      ]);

      let videoTrack = videoResult.status === 'fulfilled' ? videoResult.value.getVideoTracks()[0] : null;
      let audioTrack = audioResult.status === 'fulfilled' ? audioResult.value.getAudioTracks()[0] : null;

      if (videoTrack) localVideoDeviceIdRef.current = videoTrack.getSettings().deviceId || null;

      const device = await getMediasoupDevice();

      if (videoTrack && !device.canProduce('video')) {
        videoTrack.stop();
        videoTrack = null;
      }

      if (audioTrack && !device.canProduce('audio')) {
        audioTrack.stop();
        audioTrack = null;
      }

      if (!videoTrack && !audioTrack) throw new Error('No camera or microphone available');

      const audioEnabled = !videoTrack && Boolean(audioTrack);

      if (audioTrack) audioTrack.enabled = audioEnabled;

      localStreamRef.current = new MediaStream([videoTrack, audioTrack].filter(Boolean));
      localVideoEnabledRef.current = Boolean(videoTrack);
      localAudioEnabledRef.current = audioEnabled;

      const sendTransport = await createParticipantSendTransport(camId, publisherId);
      await publishLocalParticipantTracks(sendTransport, camId, publisherId);

      const response = await emitMedia('media_join_participant', { camId, publisherId });

      const participant = {
        id: camId,
        displayName,
        source: 'mediasoup-participant',
        opened: true,
        kind: 'mediasoup-participant',
        hasVideo: Boolean(response.participant.hasVideo),
        hasAudio: Boolean(response.participant.hasAudio),
        videoEnabled: Boolean(response.participant.videoEnabled),
        audioEnabled: Boolean(response.participant.audioEnabled),
        isReconnecting: false,
        isLocal: true
      };

      setCams((current) => [...current.filter((cam) => cam.id !== camId), participant]);
      isParticipantRef.current = true;
      setIsParticipant(true);
    } catch (error) {
      try {
        if (socket?.connected && localCamIdRef.current) await emitMedia('media_release_publisher', { camId, publisherId });
      } catch {}

      closeLocalParticipantMedia();
      localCamIdRef.current = null;
      localPublisherIdRef.current = null;
      isParticipantRef.current = false;

      throw error;
    } finally {
      setIsJoiningParticipant(false);
    }
  };

  const leaveParticipant = async () => {
    const camId = localCamIdRef.current;
    const publisherId = localPublisherIdRef.current;

    isParticipantRef.current = false;
    setIsParticipant(false);

    if (camId) setCams((current) => current.filter((cam) => cam.id !== camId));

    closeLocalParticipantMedia();

    localCamIdRef.current = null;
    localPublisherIdRef.current = null;

    try {
      if (socket?.connected && camId && publisherId) await emitMedia('media_release_publisher', { camId, publisherId });
    } catch (error) {
      console.error('Unable to release participant', error);
    }
  };

  const applyRecordingState = (recording) => {
    const active = Boolean(recording?.active);

    setIsRecording(active);
    setRecordedCamIds(active ? (recording.camIds || []).map(String) : []);
    setRecordingStartedAt(active ? recording.startedAt ?? null : null);
  };

  const startMeetRecording = async () => {
    const camIds = cams
      .filter((cam) => cam.source === 'mediasoup-participant' && cam.hasVideo && cam.videoEnabled && !cam.isReconnecting)
      .map((cam) => cam.id);

    if (!camIds.length) {
      const error = new Error('No participant available for recording');
      error.code = 'no_recordable_participant';
      throw error;
    }

    const response = await emitMedia('media_start_recording', { camIds });

    applyRecordingState(response.recording);

    return response.recording;
  };

  const stopMeetRecording = async () => {
    const response = await emitMedia('media_stop_recording');

    applyRecordingState(response.recording);

    return response.recording;
  };

  const setParticipantVideoEnabled = async (enabled) => {
    const camId = localCamIdRef.current;
    const publisherId = localPublisherIdRef.current;

    if (!camId || !publisherId || !sendTransportRef.current) throw new Error('Participant is not ready');

    if (!enabled && isRecording && recordedCamIds.includes(camId)) {
      const error = new Error('Camera cannot be disabled while recording');
      error.code = 'recording_camera_locked';
      throw error;
    }

    if (!enabled) {
      const producer = localProducersRef.current.video;
      if (!producer) return;

      await emitMedia('media_set_producer_enabled', { camId, publisherId, kind: 'video', enabled: false });

      localStreamRef.current?.getVideoTracks().forEach((track) => {
        track.stop();
        localStreamRef.current.removeTrack(track);
      });

      localVideoEnabledRef.current = false;
      refreshLocalVideo();
      return;
    }

    const videoConstraints = localVideoDeviceIdRef.current
      ? { deviceId: { exact: localVideoDeviceIdRef.current } }
      : { facingMode: { ideal: 'environment' } };

    const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
    const videoTrack = stream.getVideoTracks()[0];

    if (!videoTrack) throw new Error('No camera available');

    localVideoDeviceIdRef.current = videoTrack.getSettings().deviceId || localVideoDeviceIdRef.current;

    try {
      let producer = localProducersRef.current.video;

      if (producer && !producer.closed) {
        await producer.replaceTrack({ track: videoTrack });
      } else {
        producer = await sendTransportRef.current.produce({
          track: videoTrack,
          stopTracks: false,
          appData: { camId, publisherId, enabled: true }
        });

        localProducersRef.current.video = producer;
      }

      if (!localStreamRef.current) localStreamRef.current = new MediaStream();

      localStreamRef.current.getVideoTracks().forEach((track) => {
        track.stop();
        localStreamRef.current.removeTrack(track);
      });

      localStreamRef.current.addTrack(videoTrack);

      await emitMedia('media_set_producer_enabled', { camId, publisherId, kind: 'video', enabled: true });

      localVideoEnabledRef.current = true;
      refreshLocalVideo();
    } catch (error) {
      videoTrack.stop();
      throw error;
    }
  };

  const setParticipantAudioEnabled = async (enabled) => {
    const camId = localCamIdRef.current;
    const publisherId = localPublisherIdRef.current;

    if (!camId || !publisherId || !sendTransportRef.current) throw new Error('Participant is not ready');

    let producer = localProducersRef.current.audio;
    let audioTrack = localStreamRef.current?.getAudioTracks()[0];

    if (!enabled) {
      if (!producer) return;

      await emitMedia('media_set_producer_enabled', { camId, publisherId, kind: 'audio', enabled: false });

      if (audioTrack) audioTrack.enabled = false;

      localAudioEnabledRef.current = false;
      return;
    }

    if (!audioTrack || audioTrack.readyState !== 'live') {
      const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
      audioTrack = stream.getAudioTracks()[0];

      if (!audioTrack) throw new Error('No microphone available');

      if (!localStreamRef.current) localStreamRef.current = new MediaStream();

      localStreamRef.current.getAudioTracks().forEach((track) => {
        track.stop();
        localStreamRef.current.removeTrack(track);
      });

      localStreamRef.current.addTrack(audioTrack);

      if (producer && !producer.closed) {
        await producer.replaceTrack({ track: audioTrack });
      } else {
        producer = await sendTransportRef.current.produce({
          track: audioTrack,
          stopTracks: false,
          codecOptions: { opusDtx: false },
          appData: { camId, publisherId, enabled: true }
        });

        localProducersRef.current.audio = producer;
      }
    }

    audioTrack.enabled = true;

    await emitMedia('media_set_producer_enabled', { camId, publisherId, kind: 'audio', enabled: true });

    localAudioEnabledRef.current = true;
  };

  const refreshCamMedia = (camId) => {
    const camConsumers = consumersRef.current[camId] || {};
    const videoEl = videoElsRef.current[camId];
    const audioEl = audioElsRef.current[camId];

    if (videoEl && localCamIdRef.current !== camId) {
      const videoConsumer = camConsumers.video;
      videoEl.srcObject = videoConsumer ? new MediaStream([videoConsumer.track]) : null;

      const p = videoEl.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    if (audioEl) {
      const audioConsumer = camConsumers.audio;
      audioEl.srcObject = audioConsumer ? new MediaStream([audioConsumer.track]) : null;

      const p = audioEl.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  };

  const cleanupCamConsumer = (camId, kind) => {
    const camConsumers = consumersRef.current[camId];
    const consumer = camConsumers?.[kind];

    if (!consumer) return;

    try { consumer.close(); } catch {}
    delete camConsumers[kind];

    if (!Object.keys(camConsumers).length) delete consumersRef.current[camId];

    refreshCamMedia(camId);
  };

  const cleanupCam = (camId) => {
    const camConsumers = consumersRef.current[camId] || {};

    Object.values(camConsumers).forEach((consumer) => {
      try {
        consumer.close();
      } catch {}
    });

    delete consumersRef.current[camId];

    const recvTransport = recvTransportsRef.current[camId];
    if (recvTransport) {
      const transportId = recvTransport.id;

      try {
        recvTransport.close();
      } catch {}

      delete recvTransportsRef.current[camId];

      if (socket?.connected) socket.emit('media_close_recv_transport', { transportId });
    }

    const videoEl = videoElsRef.current[camId];
    if (videoEl) videoEl.srcObject = null;

    const audioEl = audioElsRef.current[camId];
    if (audioEl) audioEl.srcObject = null;

    camAlertedRef.current[camId] = false;
  };

  const disconnectAll = () => {
    const camIds = new Set([
      ...Object.keys(recvTransportsRef.current),
      ...Object.keys(consumersRef.current)
    ]);

    camIds.forEach((camId) => cleanupCam(camId));
  };

  const connectMediasoupCam = async (camId, kinds=['video']) => {
    if (connectingCamsRef.current[camId]) return;

    connectingCamsRef.current[camId] = true;
    camAlertedRef.current[camId] = false;

    try {
      const videoEl = videoElsRef.current[camId];
      if (!videoEl) throw new Error(`cam ${camId}: video element not found`);

      const device = await getMediasoupDevice();
      let recvTransport = recvTransportsRef.current[camId];

      if (!recvTransport) {
        const transportResponse = await emitMedia('media_create_recv_transport', { camId });
        recvTransport = device.createRecvTransport(transportResponse.transportOptions);
        recvTransportsRef.current[camId] = recvTransport;

        recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
          try {
            await emitMedia('media_connect_recv_transport', {
              transportId: recvTransport.id,
              dtlsParameters
            });

            callback();
          } catch (error) {
            errback(error);
          }
        });

        recvTransport.on('connectionstatechange', (state) => {
          console.log(`Mediasoup viewer connection state [camId=${camId}]: ${state}`);
          if (state === 'failed') raiseCamError(camId, 'mediasoup transport failed');
        });
      }

      if (!consumersRef.current[camId]) consumersRef.current[camId] = {};

      for (const kind of kinds) {
        if (consumersRef.current[camId][kind]) continue;

        const consumeResponse = await emitMedia('media_consume', {
          camId,
          transportId: recvTransport.id,
          rtpCapabilities: device.recvRtpCapabilities,
          kind
        });

        const consumer = await recvTransport.consume(consumeResponse.consumerOptions);
        consumersRef.current[camId][kind] = consumer;

        await emitMedia('media_resume_consumer', { consumerId: consumer.id });
      }

      refreshCamMedia(camId);
    } catch (e) {
      cleanupCam(camId);
      throw e;
    } finally {
      delete connectingCamsRef.current[camId];
    }
  };

  const reconnectParticipant = async () => {
    const camId = localCamIdRef.current;
    const publisherId = localPublisherIdRef.current;

    if (!isParticipantRef.current || !camId || !publisherId) return false;

    closeLocalParticipantTransport();

    try {
      await emitMedia('media_reclaim_publisher', {
        camId,
        publisherId,
        displayName: getDisplayName()
      });
    } catch (error) {
      if (error.code !== 'publisher_grace_expired') throw error;

      console.log(`Participant session expired [camId=${camId}]`);

      isParticipantRef.current = false;
      setIsParticipant(false);

      setCams((current) => current.filter((cam) => cam.id !== camId));

      closeLocalParticipantMedia();

      localCamIdRef.current = null;
      localPublisherIdRef.current = null;

      return false;
    }

    const sendTransport = await createParticipantSendTransport(camId, publisherId);
    await publishLocalParticipantTracks(sendTransport, camId, publisherId);

    setCams((current) => current.map((cam) => cam.id === camId ? { ...cam, isReconnecting: false } : cam));

    return true;
  };

  const loadParticipants = useCallback(async () => {
    const response = await emitMedia('media_get_participants');

    const participants = (response.participants || []).map((participant) => ({
      id: String(participant.camId),
      displayName: participant.displayName,
      source: 'mediasoup-participant',
      opened: true,
      kind: 'mediasoup-participant',
      hasVideo: Boolean(participant.hasVideo),
      hasAudio: Boolean(participant.hasAudio),
      videoEnabled: Boolean(participant.videoEnabled),
      audioEnabled: Boolean(participant.audioEnabled),
      isReconnecting: Boolean(participant.isReconnecting),
      isLocal: isParticipantRef.current && localCamIdRef.current === String(participant.camId)
    }));

    setCams(participants);
    setAutoConnect(true);

    return participants;
  }, [emitMedia]);

  const connectAll = useCallback(async () => {
    try {
      setIsConnecting(true);

      const results = await Promise.allSettled(cams.map((c) => {
        if (c.isLocal) return Promise.resolve();
        if (c.source !== 'mediasoup-participant') return connectMediasoupCam(c.id);

        const kinds = [];
        if (c.hasVideo) kinds.push('video');
        if (c.hasAudio) kinds.push('audio');

        if (!kinds.length) return Promise.resolve();

        return connectMediasoupCam(c.id, kinds);
      }));

      const failures = results
        .map((r, idx) => ({ r, camId: cams[idx]?.id }))
        .filter((x) => x.r.status === "rejected");

      if (failures.length) {
        console.warn(
          "Cam failures:",
          failures.map((f) => ({ cam: f.camId, err: String(f.r.reason) }))
        );
        updateStepsStatus(null, null, 'error');
      } else {
        updateStepsStatus(null, null, 'complete');
      }

    } catch (e) {
      console.error(e);
    } finally {
      setIsConnecting(false);
    }
  }, [cams]);

  useEffect(() => {
    if (!socket) return;

    if (meetMode) {
      const upsertParticipant = (participant) => {
        setCams((current) => {
          const nextParticipant = {
            id: String(participant.camId),
            displayName: participant.displayName,
            source: 'mediasoup-participant',
            opened: true,
            kind: 'mediasoup-participant',
            hasVideo: Boolean(participant.hasVideo),
            hasAudio: Boolean(participant.hasAudio),
            videoEnabled: Boolean(participant.videoEnabled),
            audioEnabled: Boolean(participant.audioEnabled),
            isReconnecting: Boolean(participant.isReconnecting),
            isLocal: isParticipantRef.current && localCamIdRef.current === String(participant.camId)
          };

          const exists = current.some((cam) => cam.id === nextParticipant.id);
          if (!exists) return [...current, nextParticipant];

          return current.map((cam) => cam.id === nextParticipant.id ? { ...cam, ...nextParticipant } : cam);
        });

        setAutoConnect(true);
      };

      const handleConsumerClosed = ({ camId, kind }) => {
        cleanupCamConsumer(String(camId), kind);
      };

      const handleParticipantReconnecting = ({ camId }) => {
        setCams((current) => current.map((cam) => cam.id === String(camId) ? { ...cam, isReconnecting: true } : cam));
      };

      const handleParticipantReleased = ({ camId }) => {
        cleanupCam(String(camId));
        setCams((current) => current.filter((cam) => cam.id !== String(camId)));
      };

      const handleRecordingState = (recording) => {
        applyRecordingState(recording);
      };

      const handleDisconnect = () => {
        if (!isParticipantRef.current || !localCamIdRef.current) return;

        setCams((current) => current.map((cam) => cam.id === localCamIdRef.current ? { ...cam, isReconnecting: true } : cam));
      };

      const handleConnect = async () => {
        try {
          disconnectAll();

          if (isParticipantRef.current) await reconnectParticipant();

          await loadParticipants();

          const recordingResponse = await emitMedia('media_get_recording_state');
          applyRecordingState(recordingResponse.recording);
        } catch (error) {
          console.error('Unable to restore meet connection', error);
        }
      };

      socket.on('disconnect', handleDisconnect);
      socket.on('media_consumer_closed', handleConsumerClosed);
      socket.on('media_participant_available', upsertParticipant);
      socket.on('media_participant_updated', upsertParticipant);
      socket.on('media_participant_reconnecting', handleParticipantReconnecting);
      socket.on('media_participant_released', handleParticipantReleased);
      socket.on('media_recording_state', handleRecordingState);
      socket.on('connect', handleConnect);

      if (socket.connected) handleConnect();

      return () => {
        socket.off('disconnect', handleDisconnect);
        socket.off('media_consumer_closed', handleConsumerClosed);
        socket.off('media_participant_available', upsertParticipant);
        socket.off('media_participant_updated', upsertParticipant);
        socket.off('media_participant_reconnecting', handleParticipantReconnecting);
        socket.off('media_participant_released', handleParticipantReleased);
        socket.off('media_recording_state', handleRecordingState);
        socket.off('connect', handleConnect);
      };
    }

    const handleConsumerClosed = ({ camId }) => {
      console.log(`Mediasoup consumer closed [camId=${camId}]`);
      cleanupCam(camId);
      if (streamReleasedRef.current[camId]) return;
      setIsReconnectingStream(true);
    };

    const handleStreamAvailable = ({ camId }) => {
      console.log(`Mediasoup stream available [camId=${camId}]`);
      delete streamReleasedRef.current[camId];
      setIsReconnectingStream(false);
      detectCameras(false).catch((error) => console.error(error));
    };

    const handleStreamReleased = ({ camId, reason }) => {
      console.log(`Mediasoup stream released [camId=${camId}, reason=${reason}]`);
      streamReleasedRef.current[camId] = true;
      cleanupCam(camId);
      setIsReconnectingStream(false);
      setIsConnecting(false);
      setAutoConnect(false);
      setCams([]);
      updateStepsStatus('upcoming', 'upcoming', 'upcoming');
    };

    socket.on('media_consumer_closed', handleConsumerClosed);
    socket.on('media_stream_available', handleStreamAvailable);
    socket.on('media_stream_released', handleStreamReleased);

    return () => {
      socket.off('media_consumer_closed', handleConsumerClosed);
      socket.off('media_stream_available', handleStreamAvailable);
      socket.off('media_stream_released', handleStreamReleased);
    };
  }, [socket, detectCameras, meetMode, loadParticipants]);

  // useEffect needed to reflect changes on cams (not possible directly in detectCameras)
  useEffect(() => {
    if (!autoConnect) return;
    if (!cams.length) {
      setAutoConnect(false);
      return;
    }
    setAutoConnect(false);
    connectAll().catch(() => {});
  }, [autoConnect, cams, connectAll]);

  useEffect(() => {
    return () => {
      if (localCaptureClaimedRef.current && socket?.connected) {
        socket.emit('media_release_local_capture', {});
        localCaptureClaimedRef.current = false;
      }

      if (localCamIdRef.current && localPublisherIdRef.current && socket?.connected) {
        socket.emit('media_release_publisher', {
          camId: localCamIdRef.current,
          publisherId: localPublisherIdRef.current
        });
      }

      closeLocalParticipantMedia();

      disconnectAll();
    };
  }, [socket]);

  const contextValue = {
    cams,
    isConnecting,
    isError,
    isReconnectingStream,
    layoutPreset,
    setLayoutPreset,
    registerVideoEl,
    registerAudioEl,
    detectCameras,
    steps,
    scheduledJob,
    setScheduledJob,
    clearScheduledJobLocal,
    currentJob,
    isParticipant,
    isJoiningParticipant,
    joinParticipant,
    leaveParticipant,
    setParticipantVideoEnabled,
    setParticipantAudioEnabled,
    isRecording,
    recordedCamIds,
    recordingStartedAt,
    startMeetRecording,
    stopMeetRecording,
  };

  return <WebRTCContext.Provider value={contextValue}>{children}</WebRTCContext.Provider>;
};

export const useWebRTC = () => {
  return useContext(WebRTCContext);
};